import React, {useContext, useEffect, useState} from 'react';
import {Form, Input, Modal, Select} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
import {CloseOutlined} from '@ant-design/icons';
import {Appointment, ClinicResponse, Patient} from "../../api-client";
import {AlertContext} from "../../context/AlertContext";
import dayjs from "dayjs";
import {getClinicsApi, getPatientApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";

interface PatientAppointmentModalProps {
    mode: 'create' | 'edit' | '';
    appointment: Appointment | undefined;
    patient: Patient;
    appointments: Appointment[];
    loadData: Function;
    isModalOpen: boolean;
    setIsModalOpen: Function;
}

const PatientAppointmentModal = ({
                                     mode, appointments, appointment, patient,
                                     loadData, isModalOpen, setIsModalOpen
                                 }: PatientAppointmentModalProps) => {

    const intl = useIntl();

    const {setVisible} = useContext(AlertContext);

    const [duplicatedName, setDuplicatedName] = useState<boolean>(false);

    const [name, setName] = useState<string>('');

    const [clinics, setClinics] = useState<ClinicResponse[]>([]);
    const [clinicId, setClinicId] = useState<string>('');

    const patientApi = getPatientApi();
    const clinicsApi = getClinicsApi();

    // A clinic must be chosen only when the organization has more than one clinic.
    // Single-clinic orgs auto-set it on the backend; the field stays hidden.
    const clinicRequired = clinics.length > 1;

    useEffect(() => {
        let formName = appointment?.name!;
        if (mode === 'create') {
            formName = dayjs().format('DD.MM.YYYY');
        }

        setName(formName);
        form.setFieldValue('formAppointmentName', formName);

        clinicsApi.listClinics()
            .then(res => {
                const list = res.data ?? [];
                setClinics(list);
                // Edit: prefill the appointment's clinic. Create: preselect the
                // sole clinic when there is exactly one.
                const initial = mode === 'edit'
                    ? (appointment?.clinicId ?? '')
                    : (list.length === 1 ? list[0].id : '');
                setClinicId(initial);
                form.setFieldValue('formClinic', initial || undefined);
            })
            .catch(err => logger.error('Failed to load clinics:', err));
    }, [isModalOpen]);

    const handleCancel = () => {
        setDuplicatedName(false);
        setIsModalOpen(false);
    }

    const handleOk = async () => {
        if (!patient.id) {
            return;
        }

        setDuplicatedName(false);

        await form.validateFields();

        const isUniqueName = appointments.filter(appointmentItem => appointmentItem.name === name).length === 0;

        if ((!isUniqueName && mode === 'create')
        || (!isUniqueName && mode === 'edit' && name !== appointment?.name)) {
            setDuplicatedName(true);

            return;
        }

        // Only send clinicId when the org has multiple clinics; single/zero-clinic
        // orgs let the backend auto-set (or leave null).
        const clinicIdToSend = clinicRequired ? clinicId : undefined;

        try {
            if (mode === 'create') {
                await patientApi.createPatientAppointment(patient.id, {name, clinicId: clinicIdToSend})
            } else {
                await patientApi.updatePatientAppointment(patient.id, appointment?.id!, {name, clinicId: clinicIdToSend})
            }

            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            loadData();
            setDuplicatedName(false);
            setIsModalOpen(false);
        } catch (err) {
            logger.log(err);
        }
    }

    const modalTitle = mode === 'create' ? intl.formatMessage({id: 'add_appointment'}) :
        intl.formatMessage({id: 'edit_appointment'});

    const [form] = Form.useForm();

    return (
        <Modal
            transitionName=""
            className={'dentobook-modal'}
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={modalTitle}
            width={800}
            okText={mode === 'create' ? <FormattedMessage id="create_button_text"/> : <FormattedMessage id="confirm"/>}
            cancelText={<FormattedMessage id="cancel"/>}
            closeIcon={<CloseOutlined className={'icon-24px'} onClick={handleCancel}/>}
            open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
            <Form layout="vertical" form={form}>
                <Form.Item
                    name="formAppointmentName"
                    className={"dentobook-form-item dentobook-simple-input mb-32"}
                    initialValue={name}
                    label={<FormattedMessage id="name"/>}
                    rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                    <Input
                        bordered={false}
                        value={name}
                        onChange={e => setName(e.target.value)}/>
                    {duplicatedName ? <span className="color-error"><FormattedMessage id="duplicated_appointment" /></span> : null}
                </Form.Item>
                {clinicRequired ? (
                    <Form.Item
                        name="formClinic"
                        className={"dentobook-form-item dentobook-simple-input mb-32"}
                        label={<FormattedMessage id="clinic"/>}
                        rules={[{required: true, message: <FormattedMessage id="clinic_required"/>}]}>
                        <Select
                            placeholder={intl.formatMessage({id: 'select_clinic'})}
                            value={clinicId || undefined}
                            onChange={value => {
                                setClinicId(value);
                                form.setFieldValue('formClinic', value);
                            }}
                            options={clinics.map(c => ({value: c.id, label: c.name}))}
                        />
                    </Form.Item>
                ) : null}
            </Form>
        </Modal>
    );
};

export default PatientAppointmentModal;